import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Tag,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Send,
  User,
  Shield,
  Building2,
  RefreshCw,
} from "lucide-react";
import "../styles/admin-base.css";
import "../styles/tickets.css";
import api, { listDepartmentAccounts, assignTicketHandler } from "../api/admin";
import { createTicketSocket, joinTicketRoom, leaveTicketRoom } from "../../src/realtime/ticketSocket";

const STATUS_TABS = [
  { id: "open", label: "Open", status: "open" },
  { id: "ticketAssigned", label: "Ticket Assigned", status: "ticket_assigned" },
  { id: "inProgress", label: "In Progress", status: "in_progress" },
  { id: "closed", label: "Closed", status: "closed" },
  { id: "overdue", label: "Overdue", status: "overdue" },
];

const STATUS_TONES = {
  closed: "is-green",
  in_progress: "is-blue",
  open: "is-orange",
  ticket_assigned: "is-purple",
  assigned: "is-blue",
  overdue: "is-red",
};

const STATUS_LABELS = {
  closed: "Closed",
  open: "Open",
  ticket_assigned: "Ticket Assigned",
  in_progress: "In Progress",
  assigned: "Assigned",
  overdue: "Overdue",
};

const toTitleCase = (value) => (value ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) : "");

const resolveDisplayStatus = (ticket) => {
  if (!ticket) {
    return null;
  }
  if (
    (ticket.status === "open" || ticket.status === "in_progress" || ticket.status === "ticket_assigned") &&
    ticket.slaDeadline &&
    new Date(ticket.slaDeadline) < new Date()
  ) {
    return "overdue";
  }
  return ticket.status;
};

const DEFAULT_OVERVIEW = {
  statuses: {
    open: 0,
    ticketAssigned: 0,
    inProgress: 0,
    closed: 0,
    overdue: 0,
    total: 0,
  },
  categories: [],
  priorities: [],
  assignees: [],
};

const DEFAULT_FILTERS = {
  search: "",
  category: "all",
  priority: "all",
  assignedTo: "all",
};

const TABLE_PAGE_SIZE = 5;

const DEPARTMENT_OPTIONS = [
  { key: "medical", label: "Medical Department" },
  { key: "legal", label: "Legal Department" },
  { key: "education", label: "Education Department" },
  { key: "emergency-support", label: "Emergency Support" },
  { key: "finance", label: "Finance Department" },
];

const DEPT_LABEL_TO_KEY = Object.fromEntries(
  DEPARTMENT_OPTIONS.map((opt) => [opt.label.toLowerCase(), opt.key])
);

const getTicketDeptKey = (ticket) => {
  if (ticket.departmentAssignment?.key) return ticket.departmentAssignment.key;
  if (!ticket.department) return null;
  const lower = ticket.department.toLowerCase().trim();
  return DEPT_LABEL_TO_KEY[lower] || lower.replace(/\s+department$/i, "").replace(/\s+/g, "-");
};

const DEPT_DISPLAY_NAMES = {
  legal: "Legal Dept.",
  medical: "Medical Dept.",
  education: "Education Dept.",
  emergency: "Emergency Dept.",
};

const DEPT_ROLE_NAMES = {
  legal: "Legal Department",
  medical: "Medical Department",
  education: "Education Department",
  emergency: "Emergency Support",
  finance: "Finance Department",
};

const toNumericId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const normalizeSenderRole = (message) => {
  const rawRole = message?.senderRole || message?.sender_role || message?.authorType;
  if (!rawRole) {
    if (message?.authorDeptAccountId || message?.authorDeptId) return "department";
    if (message?.authorAdminId || message?.isAdmin) return "admin";
    if (message?.authorUserId || message?.isUser) return "user";
    return "system";
  }
  const role = String(rawRole).toLowerCase();
  if (role === "dept") return "department";
  if (role.includes("department")) return "department";
  if (role.includes("admin")) return "admin";
  if (role.includes("user") || role.includes("member") || role === "client") return "user";
  return "system";
};

const formatAdminRole = (role) => {
  if (!role) return "Administrator";
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes("super")) return "Super Administrator";
  if (normalized.includes("ticket")) return "Ticket Administrator";
  return toTitleCase(normalized);
};

const getAdminRoleTone = (role) => {
  if (!role) return "default";
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes("super")) return "super";
  if (normalized.includes("ticket")) return "ticket";
  return "default";
};

const formatDepartmentRole = (department) => {
  if (!department) return "Department";
  const normalized = String(department).trim().toLowerCase();
  return DEPT_ROLE_NAMES[normalized] || toTitleCase(normalized);
};

const resolveMessagePresenter = (message, currentStaffAuthorIds) => {
  const senderRole = normalizeSenderRole(message);
  const isSystem = senderRole === "system" || message.message?.startsWith("[System]");

  if (isSystem) {
    return {
      senderType: "system",
      senderLabel: "System",
      senderName: "System",
      senderRoleLabel: null,
      adminRoleTone: null,
      isOutgoingStaffMessage: false,
    };
  }

  if (senderRole === "department") {
    const deptId = toNumericId(
      message.authorDeptAccountId ?? message.authorDeptId ?? message.senderId ?? message.sender_id
    );
    return {
      senderType: "dept",
      senderLabel: `${message.authorDeptName || "Department Handler"} | ${formatDepartmentRole(message.authorDeptDepartment)}`,
      senderName: message.authorDeptName || "Department Handler",
      senderRoleLabel: formatDepartmentRole(message.authorDeptDepartment),
      adminRoleTone: null,
      isOutgoingStaffMessage: deptId != null && currentStaffAuthorIds.deptIds.has(deptId),
    };
  }

  if (senderRole === "admin") {
    const adminId = toNumericId(message.authorAdminId ?? message.senderId ?? message.sender_id);
    return {
      senderType: "admin",
      senderLabel: `${message.authorAdminName || "Admin"} | ${formatAdminRole(message.authorAdminRole)}`,
      senderName: message.authorAdminName || "Admin",
      senderRoleLabel: formatAdminRole(message.authorAdminRole),
      adminRoleTone: getAdminRoleTone(message.authorAdminRole),
      isOutgoingStaffMessage: adminId != null && currentStaffAuthorIds.adminIds.has(adminId),
    };
  }

  return {
    senderType: "member",
    senderLabel: message.authorUserName || message.authorUserId || "Member",
    senderName: message.authorUserName || message.authorUserId || "Member",
    senderRoleLabel: null,
    adminRoleTone: null,
    isOutgoingStaffMessage: false,
  };
};

// Maps ticket category values to the department field used on dept accounts
const CATEGORY_TO_ACCOUNT_DEPT = {
  medical: "medical",
  legal: "legal",
  education: "education",
  emergency: "emergency",
  "emergency-support": "emergency",
  finance: "finance",
};

const DEPARTMENT_MESSAGE_TYPE = "department-assignment";

const isDepartmentAssignmentMessage = (message) => {
  if (typeof message !== "string") {
    return false;
  }
  try {
    const parsed = JSON.parse(message);
    return parsed?.type === DEPARTMENT_MESSAGE_TYPE;
  } catch (error) {
    return false;
  }
};

const sanitizeMessageText = (message) => {
  if (message == null) {
    return null;
  }
  if (typeof message !== "string") {
    return message;
  }
  if (isDepartmentAssignmentMessage(message)) {
    return null;
  }
  const trimmed = message.trim();
  return trimmed || null;
};

const stripTicketSystemTags = (value) => {
  if (value == null) return null;
  if (typeof value !== "string") return String(value);

  const cleaned = value
    .replace(/\[(?:Category:[^\]]+|System)\]\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || null;
};

const sanitizeAiResponse = (response) => {
  if (!response) {
    return null;
  }
  const sanitizedMessage = sanitizeMessageText(response.message);
  if (!sanitizedMessage) {
    return null;
  }
  return {
    ...response,
    message: sanitizedMessage,
  };
};

const normalizeDepartmentAssignment = (assignment) => {
  if (!assignment) {
    return null;
  }
  let payload = assignment;
  if (typeof assignment === "string") {
    try {
      const parsed = JSON.parse(assignment);
      if (parsed?.type && parsed.type !== DEPARTMENT_MESSAGE_TYPE) {
        return null;
      }
      payload = parsed;
    } catch (error) {
      return null;
    }
  }
  if (payload?.type && payload.type !== DEPARTMENT_MESSAGE_TYPE) {
    return null;
  }
  const rawKey = payload.key || payload.department || payload.departmentKey || payload.code;
  if (!rawKey) {
    return null;
  }
  const normalizedKey = rawKey.toString().trim().toLowerCase().replace(/\s+/g, "-");
  const matchedOption = DEPARTMENT_OPTIONS.find((option) => option.key === normalizedKey);
  const label = payload.label
    || payload.departmentLabel
    || matchedOption?.label
    || toTitleCase(normalizedKey.replace(/-/g, " "));
  return {
    key: matchedOption?.key || normalizedKey,
    label,
    assignedAt: payload.assignedAt ?? null,
    assignedBy: payload.assignedBy ?? null,
  };
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    return value;
  }
};

const formatDateShort = (value) => {
  if (!value) {
    return "";
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return value;
  }
};

function TicketsSelect({
  menuId,
  value,
  options,
  placeholder,
  disabled = false,
  isOpen,
  onToggle,
  onSelect,
  className = "",
}) {
  const selected = options.find((option) => String(option.value) === String(value));
  const label = selected?.label || placeholder;

  return (
    <div className={`tickets-select ${isOpen ? "is-open" : ""} ${className}`.trim()}>
      <button
        type="button"
        className="tickets-select__trigger"
        onClick={onToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <ChevronDown size={16} />
      </button>

      {isOpen ? (
        <div className="tickets-select__menu" role="listbox" aria-label={menuId}>
          {options.length === 0 ? (
            <div className="tickets-select__empty">No options available.</div>
          ) : (
            options.map((option) => {
              const isActive = String(option.value) === String(value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`tickets-select__option ${isActive ? "is-active" : ""}`}
                  onClick={() => onSelect(option.value)}
                  disabled={Boolean(option.disabled)}
                >
                  <span>{option.label}</span>
                  {isActive ? <Check size={14} /> : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function Tickets() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(DEFAULT_OVERVIEW);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState(STATUS_TABS[0].id);
  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSampleData, setIsSampleData] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [assigningTicketId, setAssigningTicketId] = useState(null);
  const [deptAccounts, setDeptAccounts] = useState([]);
  const [filteredCounts, setFilteredCounts] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const activeFilterCount = [
    filters.category !== "all",
    filters.priority !== "all",
    filters.assignedTo !== "all",
  ].filter(Boolean).length;

  // Modal conversation state
  const [modalMessages, setModalMessages] = useState([]);
  const [modalMessagesLoading, setModalMessagesLoading] = useState(false);
  const [modalReplyText, setModalReplyText] = useState("");
  const [modalReplySending, setModalReplySending] = useState(false);

  const currentStaffAuthorIds = useMemo(() => {
    if (typeof window === "undefined" || !window.localStorage) {
      return { adminIds: new Set(), deptIds: new Set() };
    }
    try {
      const raw = window.localStorage.getItem("adminUser");
      const parsed = raw ? JSON.parse(raw) : null;
      const toValidId = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
      };

      const adminCandidates = [parsed?.id, parsed?.adminId, parsed?.admin?.id]
        .map(toValidId)
        .filter(Boolean);
      const deptCandidates = [
        parsed?.departmentAccountId,
        parsed?.deptAccountId,
        parsed?.departmentAccount?.id,
        parsed?.departmentAccount?.deptAccountId,
        parsed?.deptAccount?.id,
      ]
        .map(toValidId)
        .filter(Boolean);

      return {
        adminIds: new Set(adminCandidates),
        deptIds: new Set(deptCandidates),
      };
    } catch (_error) {
      return { adminIds: new Set(), deptIds: new Set() };
    }
  }, []);
  const [modalReplyError, setModalReplyError] = useState("");
  const modalMessagesEndRef = useRef(null);
  const modalSocketRef = useRef(null);

  const enhanceTicket = useCallback((ticket) => {
    if (!ticket) {
      return ticket;
    }
    const normalizedAssignment = normalizeDepartmentAssignment(ticket.departmentAssignment);
    const sanitizedDescription = sanitizeMessageText(ticket.description);
    const sanitizedLatestMessage = sanitizeMessageText(ticket.latestMessage);
    const sanitizedAi = sanitizeAiResponse(ticket.aiResponse);
    const resolvedAssignedAdmin = ticket.assignedAdmin
      ?? (normalizedAssignment ? { name: normalizedAssignment.label } : null);
    return {
      ...ticket,
      description: sanitizedDescription ?? null,
      latestMessage: sanitizedLatestMessage ?? null,
      departmentAssignment: normalizedAssignment,
      aiResponse: sanitizedAi,
      assignedAdmin: resolvedAssignedAdmin,
    };
  }, []);

  useEffect(() => {
    if (!openDropdownId) {
      return undefined;
    }
    const handleDocumentClick = (event) => {
      if (!event.target.closest(".tickets-select")) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [openDropdownId]);

  const loadOverview = useCallback(async () => {
    try {
      const response = await api.getTicketsOverview();
      const payload = response.data ?? {};
      setOverview({
        statuses: { ...DEFAULT_OVERVIEW.statuses, ...(payload.statuses ?? {}) },
        categories: Array.isArray(payload.categories) ? payload.categories : [],
        priorities: Array.isArray(payload.priorities) ? payload.priorities : [],
        assignees: Array.isArray(payload.assignees) ? payload.assignees : [],
      });
      setIsSampleData(Boolean(payload.meta?.isSample));
    } catch (err) {
      console.error("Unable to load tickets overview", err);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const statusConfig = STATUS_TABS.find((tab) => tab.id === activeTab);
      const params = {
        pageSize: 200,
      };
      if (statusConfig?.status) {
        params.status = statusConfig.status;
      }
      if (filters.search.trim()) {
        params.query = filters.search.trim();
      }
      if (filters.category !== "all") {
        params.category = filters.category;
      }
      if (filters.priority !== "all") {
        params.priority = filters.priority;
      }
      if (filters.assignedTo !== "all") {
        params.assignedDeptAccountId = filters.assignedTo;
      }
      const response = await api.listTickets(params);
      const fetchedTickets = response.data?.results ?? [];
      const handlerFiltered = filters.assignedTo !== "all"
        ? fetchedTickets.filter((t) => String(t.assignedDeptAccount?.id) === String(filters.assignedTo))
        : fetchedTickets;
      setTickets(handlerFiltered.map((ticket) => enhanceTicket(ticket)));
      setOpenDropdownId(null);
      setMeta(response.data?.meta ?? {});
      setIsSampleData(Boolean(response.data?.meta?.isSample));
      setError(null);
    } catch (err) {
      console.error("Unable to load tickets", err);
      setError("Unable to load tickets right now. Please try again later.");
      setTickets([]);
      setOpenDropdownId(null);
      setMeta({});
      setIsSampleData(false);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, enhanceTicket, filters]);

  const loadFilteredCounts = useCallback(async () => {
    const isFiltered = filters.search.trim() || filters.category !== "all" || filters.priority !== "all" || filters.assignedTo !== "all";
    if (!isFiltered) {
      setFilteredCounts(null);
      return;
    }
    try {
      const params = { pageSize: 500 };
      if (filters.search.trim()) params.query = filters.search.trim();
      if (filters.category !== "all") params.category = filters.category;
      if (filters.priority !== "all") params.priority = filters.priority;
      if (filters.assignedTo !== "all") params.assignedDeptAccountId = filters.assignedTo;
      const response = await api.listTickets(params);
      const allTickets = response.data?.results ?? [];
      const handlerFiltered = filters.assignedTo !== "all"
        ? allTickets.filter((t) => String(t.assignedDeptAccount?.id) === String(filters.assignedTo))
        : allTickets;
      const now = new Date();
      const counts = { open: 0, ticketAssigned: 0, inProgress: 0, closed: 0, overdue: 0 };
      handlerFiltered.forEach((t) => {
        const isOverdue = (t.status === "open" || t.status === "in_progress" || t.status === "ticket_assigned") && t.slaDeadline && new Date(t.slaDeadline) < now;
        if (isOverdue) {
          counts.overdue++;
        } else if (t.status === "open") {
          counts.open++;
        } else if (t.status === "ticket_assigned") {
          counts.ticketAssigned++;
        } else if (t.status === "in_progress") {
          counts.inProgress++;
        } else if (t.status === "closed") {
          counts.closed++;
        }
      });
      setFilteredCounts(counts);
    } catch (err) {
      console.error("Unable to load filtered counts", err);
      setFilteredCounts(null);
    }
  }, [filters]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    listDepartmentAccounts().then(res => setDeptAccounts(res.data?.departmentAccounts || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      loadTickets();
    }, 200);
    return () => clearTimeout(handle);
  }, [loadTickets, filters.search, filters.category, filters.priority, filters.assignedTo, activeTab]);

  useEffect(() => {
    const handle = setTimeout(() => {
      loadFilteredCounts();
    }, 200);
    return () => clearTimeout(handle);
  }, [loadFilteredCounts]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      loadOverview(),
      loadTickets(),
      loadFilteredCounts(),
    ]);
  }, [loadOverview, loadTickets, loadFilteredCounts]);

  const categoryOptions = useMemo(() => ["all", ...overview.categories], [overview.categories]);
  const priorityOptions = useMemo(() => ["all", ...overview.priorities], [overview.priorities]);
  const assigneeOptions = useMemo(() => {
    if (filters.category === "all") return [];
    const accountDept = CATEGORY_TO_ACCOUNT_DEPT[filters.category] ?? filters.category;
    return deptAccounts
      .filter((a) => a.department === accountDept)
      .map((a) => ({ value: String(a.id), label: a.fullName || a.name || a.email }));
  }, [filters.category, deptAccounts]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "category" ? { assignedTo: "all" } : {}),
    }));
    if (name === "search") {
      setCurrentPage(1);
    }
  };

  const applyDropdownFilter = useCallback((name, value) => {
    setFilters((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "category" ? { assignedTo: "all" } : {}),
    }));
    setCurrentPage(1);
    setOpenDropdownId(null);
  }, []);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(tickets.length / TABLE_PAGE_SIZE)),
    [tickets.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * TABLE_PAGE_SIZE;
    return tickets.slice(startIndex, startIndex + TABLE_PAGE_SIZE);
  }, [currentPage, tickets]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((previous) => Math.max(1, previous - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((previous) => Math.min(totalPages, previous + 1));
  }, [totalPages]);

  const loadModalMessages = useCallback(async (ticketId) => {
    setModalMessagesLoading(true);
    try {
      const res = await api.getTicket(ticketId);
      const msgs = (res.data.messages || []).filter(
        (m) => m.message && !isDepartmentAssignmentMessage(m.message)
      );
      setModalMessages(msgs);
    } catch (err) {
      console.error("Unable to load messages", err);
      setModalMessages([]);
    } finally {
      setModalMessagesLoading(false);
    }
  }, []);

  const handleOpenTicket = useCallback((ticket) => {
    const enhanced = enhanceTicket(ticket);
    setSelectedTicket(enhanced);
    setModalMessages([]);
    setModalReplyText("");
    setModalReplyError("");
    loadModalMessages(ticket.id);
  }, [enhanceTicket, loadModalMessages]);

  const handleCloseTicket = () => {
    setSelectedTicket(null);
    setModalMessages([]);
    setModalReplyText("");
    setModalReplyError("");
  };

  const handleAssignDepartment = useCallback(async (ticketId, departmentKey) => {
    if (!ticketId || !departmentKey) {
      return;
    }
    try {
      setAssigningTicketId(ticketId);
      const response = await api.assignTicketDepartment(ticketId, { department: departmentKey });
      const assignmentPayload = response.data?.assignment ?? { key: departmentKey };
      const normalizedAssignment = normalizeDepartmentAssignment({
        ...assignmentPayload,
        key: assignmentPayload.key ?? departmentKey,
      });
      const updatedStatus = response.data?.ticket?.status;

      setTickets((previousTickets) => previousTickets.map((ticket) => {
        if (ticket.id !== ticketId) {
          return ticket;
        }
        return enhanceTicket({
          ...ticket,
          status: updatedStatus ?? ticket.status,
          departmentAssignment: normalizedAssignment,
        });
      }));

      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((prev) => (prev
          ? enhanceTicket({
              ...prev,
              status: updatedStatus ?? prev.status,
              departmentAssignment: normalizedAssignment,
            })
          : prev));
      }
    } catch (assignError) {
      console.error("Unable to assign department", assignError);
    } finally {
      setAssigningTicketId(null);
      setOpenDropdownId(null);
    }
  }, [enhanceTicket, selectedTicket]);

  const handleAssignHandler = useCallback(async (ticketId, accountId) => {
    if (!ticketId || !accountId) return;
    try {
      setAssigningTicketId(ticketId);
      const response = await assignTicketHandler(ticketId, { assignedDeptAccountId: accountId });
      const updatedTicket = response.data?.ticket;
      const fallbackAccount = deptAccounts.find((a) => String(a.id) === String(accountId));

      setTickets((prev) => prev.map((t) => {
        if (t.id !== ticketId) return t;
        return enhanceTicket({
          ...t,
          status: updatedTicket?.status || t.status,
          assignedDeptAccount: updatedTicket?.assignedDeptAccount || fallbackAccount || t.assignedDeptAccount,
        });
      }));

      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((prev) => prev ? enhanceTicket({
          ...prev,
          status: updatedTicket?.status || prev.status,
          assignedDeptAccount: updatedTicket?.assignedDeptAccount || fallbackAccount || prev.assignedDeptAccount,
        }) : prev);
      }
    } catch (err) {
      console.error("Unable to assign handler", err);
    } finally {
      setAssigningTicketId(null);
      setOpenDropdownId(null);
    }
  }, [enhanceTicket, selectedTicket, deptAccounts]);

  const [statusUpdating, setStatusUpdating] = useState(false);

  const handleQuickStatusChange = useCallback(async (ticketId, newStatus) => {
    if (!ticketId || !newStatus || statusUpdating) return;
    setStatusUpdating(true);
    try {
      await api.updateTicket(ticketId, { status: newStatus });
      setTickets((prev) => prev.map((t) =>
        t.id !== ticketId ? t : enhanceTicket({ ...t, status: newStatus })
      ));
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((prev) => prev ? enhanceTicket({ ...prev, status: newStatus }) : prev);
      }
    } catch (err) {
      console.error("Unable to update status", err);
    } finally {
      setStatusUpdating(false);
    }
  }, [enhanceTicket, selectedTicket, statusUpdating]);

  const handleModalSendReply = useCallback(async () => {
    const trimmed = modalReplyText.trim();
    if (!trimmed || !selectedTicket) return;
    setModalReplySending(true);
    setModalReplyError("");
    try {
      const activeAdminId = Array.from(currentStaffAuthorIds.adminIds)[0] || null;
      const payload = activeAdminId
        ? { message: trimmed, authorAdminId: activeAdminId }
        : { message: trimmed };
      await api.postTicketMessage(selectedTicket.id, payload);
      setModalReplyText("");
    } catch (err) {
      console.error("Failed to send reply:", err);
      setModalReplyError("Failed to send reply. Please try again.");
    } finally {
      setModalReplySending(false);
    }
  }, [modalReplyText, selectedTicket, currentStaffAuthorIds.adminIds]);

  const handleModalKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleModalSendReply();
    }
  }, [handleModalSendReply]);

  useEffect(() => {
    if (modalMessages.length) {
      modalMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [modalMessages]);

  useEffect(() => {
    const activeTicketId = selectedTicket?.id;
    if (!activeTicketId) {
      return undefined;
    }

    const socket = createTicketSocket("admin");
    if (!socket) {
      return undefined;
    }

    modalSocketRef.current = socket;

    const handleReceiveMessage = (incomingMessage) => {
      if (String(incomingMessage?.ticketId ?? "") !== String(activeTicketId)) {
        return;
      }

      if (isDepartmentAssignmentMessage(incomingMessage?.message)) {
        return;
      }

      setModalMessages((previousMessages) => {
        const alreadyExists = previousMessages.some((message) => String(message.id) === String(incomingMessage.id));
        if (alreadyExists) {
          return previousMessages;
        }
        return [...previousMessages, incomingMessage];
      });
    };

    const joinActiveTicketRoom = () => {
      joinTicketRoom(socket, activeTicketId);
    };

    socket.on("connect", joinActiveTicketRoom);
    socket.on("receive_message", handleReceiveMessage);
    joinActiveTicketRoom();

    return () => {
      leaveTicketRoom(socket, activeTicketId);
      socket.off("connect", joinActiveTicketRoom);
      socket.off("receive_message", handleReceiveMessage);
      socket.disconnect();
      modalSocketRef.current = null;
    };
  }, [selectedTicket?.id]);

  useEffect(() => {
    if (!selectedTicket) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseTicket();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [selectedTicket]);

  const summaryCards = useMemo(() => (
    [
      {
        id: "total",
        label: "Total tickets",
        value: overview.statuses.total,
        hint: "Across all queues",
        icon: Tag,
        tone: "is-blue",
      },
      {
        id: "open",
        label: "Open",
        value: overview.statuses.open ?? 0,
        hint: "Awaiting attention",
        icon: AlertCircle,
        tone: "is-orange",
      },
      {
        id: "inProgress",
        label: "In progress",
        value: overview.statuses.inProgress,
        hint: "Being worked on",
        icon: Clock,
        tone: "is-blue",
      },
      {
        id: "ticketAssigned",
        label: "Ticket Assigned",
        value: overview.statuses.ticketAssigned,
        hint: "Handler assigned",
        icon: CheckCircle2,
        tone: "is-purple",
      },
      {
        id: "overdue",
        label: "Overdue",
        value: overview.statuses.overdue ?? 0,
        hint: "Past SLA deadline",
        icon: AlertCircle,
        tone: "is-red",
      },
    ]
  ), [overview.statuses]);

  const renderTicketStatusChip = (ticket, extraClassName = "") => {
    const displayStatus = resolveDisplayStatus(ticket);
    const tone = STATUS_TONES[displayStatus] ?? "";
    const statusLabel = STATUS_LABELS[displayStatus] || (displayStatus ? toTitleCase(displayStatus) : "Unknown");
    const icon = tone === "is-green" ? <CheckCircle2 size={14} /> : tone === "is-orange" ? <AlertCircle size={14} /> : <Clock size={14} />;
    return (
      <span className={`admin-chip ${tone} ${extraClassName}`.trim()}>
        {icon}
        <span>{statusLabel}</span>
      </span>
    );
  };

  return (
    <div className="admin-page admin-stack-lg tickets-page">
      <header className="tickets-header admin-surface tickets-hero">
        <div className="tickets-hero__intro">
          <span className="tickets-hero__eyebrow">Ticket Operations</span>
          <h1>Tickets</h1>
          <p className="admin-muted">Monitor and manage incoming member support tickets.</p>
        </div>
        <div className="tickets-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${isLoading ? "is-loading" : ""}`}
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh tickets"
            title="Refresh tickets"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      <section className="tickets-overview">
        {summaryCards.map((card) => {
          const CardIcon = card.icon;
          return (
            <article key={card.id} className="tickets-overview__card">
              <span className="tickets-overview__label">{card.label}</span>
              <div className="admin-row">
                <strong className="tickets-overview__value">{card.value}</strong>
                <span className={`admin-chip ${card.tone}`}>
                  <CardIcon size={18} />
                </span>
              </div>
              <span className="tickets-overview__hint">{card.hint}</span>
            </article>
          );
        })}
      </section>

      <section className="admin-surface tickets-search-surface tickets-section">
        <div className="admin-row members-table__search-row tickets-search-row">
          <div className="tickets-search-field">
            <span className="admin-card__label">Search Tickets</span>
            <div className="admin-card tickets-search-card">
              <Search size={18} color="#64748b" />
              <input
                id="ticket-search"
                name="search"
                type="search"
                placeholder="Search by ticket ID, member, category, or status"
                value={filters.search}
                onChange={handleFilterChange}
                className="tickets-search-input"
              />
            </div>
          </div>
          <div className="id-card-queue__filter-button-wrap tickets-filter-toggle-wrap">
            <button
              type="button"
              className={`admin-button admin-button--ghost tickets-filter-toggle ${showFilters ? "is-open" : ""} ${activeFilterCount > 0 ? "is-active" : ""}`}
              onClick={() => setShowFilters((open) => !open)}
              aria-pressed={showFilters}
            >
              <Filter size={18} /> Filters
              {activeFilterCount > 0 ? (
                <span className="tickets-filter-toggle__badge">{activeFilterCount}</span>
              ) : null}
            </button>
          </div>
        </div>
        {showFilters ? (
          <div className="admin-row admin-row--filters-inline tickets-filters-inline">
            <div className="admin-field tickets-filter-field">
              <label htmlFor="ticket-category">Category</label>
              <TicketsSelect
                menuId="ticket-category"
                value={filters.category}
                options={categoryOptions.map((option) => ({
                  value: option,
                  label: option === "all" ? "All Categories" : toTitleCase(option),
                }))}
                placeholder="All Categories"
                isOpen={openDropdownId === "filters-category"}
                onToggle={() => setOpenDropdownId((prev) => (prev === "filters-category" ? null : "filters-category"))}
                onSelect={(value) => applyDropdownFilter("category", value)}
              />
            </div>
            <div className="admin-field tickets-filter-field">
              <label htmlFor="ticket-priority">Priority</label>
              <TicketsSelect
                menuId="ticket-priority"
                value={filters.priority}
                options={priorityOptions.map((option) => ({
                  value: option,
                  label: option === "all" ? "All Priorities" : toTitleCase(option),
                }))}
                placeholder="All Priorities"
                isOpen={openDropdownId === "filters-priority"}
                onToggle={() => setOpenDropdownId((prev) => (prev === "filters-priority" ? null : "filters-priority"))}
                onSelect={(value) => applyDropdownFilter("priority", value)}
              />
            </div>
            <div className="admin-field tickets-filter-field">
              <label htmlFor="ticket-assignee">Assigned To</label>
              <TicketsSelect
                menuId="ticket-assignee"
                value={filters.assignedTo}
                options={[
                  {
                    value: "all",
                    label: filters.category === "all" ? "Select a category first" : "All Handlers",
                  },
                  ...assigneeOptions,
                ]}
                placeholder="All Handlers"
                disabled={filters.category === "all"}
                isOpen={openDropdownId === "filters-assignee"}
                onToggle={() => setOpenDropdownId((prev) => (prev === "filters-assignee" ? null : "filters-assignee"))}
                onSelect={(value) => applyDropdownFilter("assignedTo", value)}
              />
            </div>
          </div>
        ) : null}
      </section>

      <section className="tickets-panel tickets-section">
        <div className="tickets-tabs" role="tablist">
          {STATUS_TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            const count = (filteredCounts ?? overview.statuses)[tab.id] ?? 0;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`tickets-tab ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{tab.label}</span>
                <span className="tickets-tab__count">{count}</span>
              </button>
            );
          })}
        </div>

        {error ? (
          <div className="tickets-error" role="alert">{error}</div>
        ) : null}

        <section className="tickets-list">
          {isLoading ? (
            <div className="admin-empty-state">Loading tickets…</div>
          ) : null}

          {!isLoading && !tickets.length ? (
            <div className="admin-empty-state">No tickets found for the selected filters.</div>
          ) : null}

          {!isLoading && paginatedTickets.map((ticket) => {
          const displayStatus = resolveDisplayStatus(ticket);
          const toneClass = STATUS_TONES[displayStatus] ?? "";
          const memberName = ticket.user?.name || ticket.user?.email || `Member #${ticket.user?.id ?? "—"}`;
          const createdLabel = formatDateShort(ticket.createdAt);
          const latestMessageLabel = ticket.latestMessageAt ? formatDate(ticket.latestMessageAt) : "";
          const assignedLabel = ticket.assignedAdmin?.name
            || ticket.assignedAdmin?.email
            || (ticket.assignedTo ? `Admin #${ticket.assignedTo}` : ticket.departmentAssignment?.label || "Unassigned");
          const rawDescription = ticket.description || ticket.latestMessage || "";
          const description = stripTicketSystemTags(rawDescription) || "No description captured for this ticket.";
          const categoryLabel = toTitleCase(ticket.category) || "Uncategorized";
          const priorityLabel = toTitleCase(ticket.priority) || "Not set";
          const departmentLabel = ticket.department || ticket.departmentAssignment?.label || "Not assigned";
          const assigningThisTicket = assigningTicketId === ticket.id;
          const ticketDeptKey = getTicketDeptKey(ticket);
          const handlersForTicket = deptAccounts.filter((acc) => acc.department === ticketDeptKey);

          return (
            <article key={ticket.id} className={`tickets-card ${toneClass}`.trim()}>
              <header className="tickets-card__header">
                <div className="tickets-card__identity">
                  <button
                    type="button"
                    className="tickets-card__number"
                    onClick={() => navigate(`/admin/ticket-detail?id=${ticket.id}`)}
                  >
                    {ticket.ticketNo ?? `TCK-${ticket.id}`}
                  </button>
                  <span className="tickets-card__member">{memberName}</span>
                </div>
                {renderTicketStatusChip(ticket, "tickets-card__status-pill")}
              </header>

              <div className="tickets-card__tags">
                <span className="tickets-tag">
                  <Tag size={12} />
                  {categoryLabel}
                </span>
                <span className="tickets-tag is-priority">{priorityLabel}</span>
              </div>

              <div className="tickets-card__body">
                <h3>{ticket.subject || "Untitled ticket"}</h3>
                <p>{description}</p>
              </div>

              <div className="tickets-card__meta-grid">
                <div>
                  <span className="tickets-card__meta-label">Opened</span>
                  <span className="tickets-card__meta-value">{createdLabel || "No timestamp"}</span>
                </div>
                <div>
                  <span className="tickets-card__meta-label">Assigned</span>
                  <span className="tickets-card__meta-value">{assignedLabel}</span>
                </div>
                <div>
                  <span className="tickets-card__meta-label">Latest update</span>
                  <span className="tickets-card__meta-value">{latestMessageLabel || "No replies yet"}</span>
                </div>
                <div>
                  <span className="tickets-card__meta-label">Department</span>
                  <span className="tickets-card__meta-value">{departmentLabel}</span>
                </div>
                <div>
                  <span className="tickets-card__meta-label">Handler</span>
                  <span className="tickets-card__meta-value">
                    {ticket.assignedDeptAccount
                      ? `${DEPT_DISPLAY_NAMES[ticket.assignedDeptAccount.department] || toTitleCase(ticket.assignedDeptAccount.department)} | ${ticket.assignedDeptAccount.name}`
                      : "Unassigned"}
                  </span>
                </div>
              </div>

              {ticket.aiSummary ? (
                <div className="tickets-card__ai is-active">
                  <div className="tickets-card__ai-title">
                    <CheckCircle2 size={18} />
                    <span>AI Summary</span>
                  </div>
                  <p className="tickets-card__ai-message">{ticket.aiSummary}</p>
                </div>
              ) : null}

              {ticket.slaDeadline ? (
                <div className="tickets-card__sla">
                  <Clock size={14} />
                  <span>SLA Deadline: {formatDate(ticket.slaDeadline)}</span>
                  {ticket.escalationLevel > 0 ? <span> | Escalation Level: {ticket.escalationLevel}</span> : null}
                </div>
              ) : null}

              <footer className="tickets-card__footer">
                <div className="tickets-card__footer-meta">
                  <span>{ticket.timeAgo ? `Updated ${ticket.timeAgo}` : "Update time unavailable"}</span>
                </div>
                <div className="tickets-card__actions">
                  {handlersForTicket.length > 0 ? (
                    <div className="tickets-card__assign">
                      <TicketsSelect
                        menuId={`ticket-card-handler-${ticket.id}`}
                        value={ticket.assignedDeptAccount?.id || ""}
                        options={[
                          {
                            value: "",
                            label: ticket.assignedDeptAccount
                              ? `${DEPT_DISPLAY_NAMES[ticket.assignedDeptAccount.department] || toTitleCase(ticket.assignedDeptAccount.department)} | ${ticket.assignedDeptAccount.name}`
                              : "Assign Handler...",
                          },
                          ...handlersForTicket.map((acc) => ({
                            value: String(acc.id),
                            label: `${DEPT_DISPLAY_NAMES[acc.department] || toTitleCase(acc.department)} | ${acc.fullName || acc.name || acc.email}`,
                          })),
                        ]}
                        placeholder="Assign Handler..."
                        disabled={assigningThisTicket}
                        isOpen={openDropdownId === `card-handler-${ticket.id}`}
                        onToggle={() => setOpenDropdownId((prev) => (prev === `card-handler-${ticket.id}` ? null : `card-handler-${ticket.id}`))}
                        onSelect={(value) => {
                          setOpenDropdownId(null);
                          if (value) {
                            handleAssignHandler(ticket.id, value);
                          }
                        }}
                        className="tickets-select--compact"
                      />
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="tickets-card__button"
                    onClick={() => handleOpenTicket(ticket)}
                  >
                    View Response
                    <ChevronRight size={16} />
                  </button>
                </div>
              </footer>
            </article>
          );
          })}
        </section>

        {!isLoading && tickets.length > 0 ? (
          <footer className="admin-row tickets-list__footer">
            <span className="admin-muted">Page {currentPage} of {totalPages}</span>
            <span className="admin-pill is-blue">Showing {paginatedTickets.length} of {tickets.length} ({meta.total || tickets.length} total)</span>
            <div className="admin-actions">
              <button
                type="button"
                className="admin-button tickets-pager-button"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <button
                type="button"
                className="admin-button tickets-pager-button"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </footer>
        ) : null}
      </section>

      {selectedTicket ? (
        (() => {
          const openedAt = formatDate(selectedTicket.createdAt) || "Unknown";
          const updatedAt = formatDate(selectedTicket.updatedAt) || openedAt;
          const memberName = selectedTicket.user?.name || selectedTicket.user?.email || selectedTicket.userName || selectedTicket.userEmail || `Member #${selectedTicket.user?.id ?? selectedTicket.userId ?? "—"}`;
          const rawDetailDescription = selectedTicket.description || selectedTicket.latestMessage || "";
          const description = typeof rawDetailDescription === "string" && rawDetailDescription.trim()
            ? rawDetailDescription.trim()
            : "No request details captured for this ticket.";
          const aiSummary = selectedTicket.aiSummary || null;
          const isClosedOrResolved = selectedTicket.status === "closed";
          const departmentLabel = selectedTicket.department || selectedTicket.departmentAssignment?.label || "Unassigned";
          const modalDeptKey = getTicketDeptKey(selectedTicket);
          const modalHandlers = deptAccounts.filter((acc) => acc.department === modalDeptKey);

          return (
            <div className="tickets-detail-modal" role="presentation">
              <div className="tickets-detail-modal__backdrop" onClick={handleCloseTicket} aria-hidden="true" />
              <div
                className="tickets-detail-modal__content"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tickets-detail-modal__title"
              >
                {/* Modal Header */}
                <header className="tickets-detail-modal__header">
                  <div className="tickets-detail-modal__identity">
                    <h2 id="tickets-detail-modal__title">{selectedTicket.ticketNo ?? `TCK-${selectedTicket.id}`}</h2>
                    {renderTicketStatusChip(selectedTicket, "tickets-detail-modal__status")}
                  </div>
                  <div className="tickets-detail-modal__timestamps">
                    <span>Opened {openedAt}</span>
                    <span>Updated {updatedAt}</span>
                  </div>
                  <button
                    type="button"
                    className="tickets-detail-modal__close"
                    onClick={handleCloseTicket}
                    aria-label="Close ticket details"
                  >
                    <X size={20} />
                  </button>
                </header>

                {/* Request Info + Quick Actions (side by side) */}
                <div className="tickets-modal__two-col">
                  {/* Request Details */}
                  <section className="tickets-modal__section--left">
                    <div className="tickets-modal__section-label">
                      Request Details
                    </div>
                    <p className="tickets-modal__subject">{selectedTicket.subject || "Untitled ticket"}</p>
                    <p className="tickets-modal__description">{description}</p>
                    <div className="tickets-modal__info-grid">
                      <div>
                        <div className="tickets-modal__info-label">Member</div>
                        <div className="tickets-modal__info-value">{memberName}</div>
                      </div>
                      <div>
                        <div className="tickets-modal__info-label">Member ID</div>
                        <div className="tickets-modal__info-value">{selectedTicket.membershipNo || selectedTicket.user?.membershipNo || "—"}</div>
                      </div>
                      <div>
                        <div className="tickets-modal__info-label">Priority</div>
                        <div className="tickets-modal__info-value">{toTitleCase(selectedTicket.priority) || "Not set"}</div>
                      </div>
                      <div>
                        <div className="tickets-modal__info-label">Category</div>
                        <div className="tickets-modal__info-value">{toTitleCase(selectedTicket.category) || "Uncategorized"}</div>
                      </div>
                      <div>
                        <div className="tickets-modal__info-label">Department</div>
                        <div className="tickets-modal__info-value">{departmentLabel}</div>
                      </div>
                      <div>
                        <div className="tickets-modal__info-label">Handler</div>
                        <div className="tickets-modal__info-value">
                          {selectedTicket.assignedDeptAccount
                            ? `${DEPT_DISPLAY_NAMES[selectedTicket.assignedDeptAccount.department] || toTitleCase(selectedTicket.assignedDeptAccount.department)} | ${selectedTicket.assignedDeptAccount.name}`
                            : "Unassigned"}
                        </div>
                      </div>
                      {selectedTicket.slaDeadline ? (
                        <div>
                          <div className="tickets-modal__info-label">SLA Deadline</div>
                          <div className="tickets-modal__info-value">{formatDate(selectedTicket.slaDeadline)}</div>
                        </div>
                      ) : null}
                    </div>
                  </section>

                  {/* Quick Actions */}
                  <section className="tickets-modal__section--right">
                    <div className="tickets-modal__section-label">
                      Quick Actions
                    </div>

                    {/* Update Status */}
                    <div className="tickets-modal__action-card">
                      <label className="tickets-modal__action-label">
                        Update Status
                      </label>
                      <TicketsSelect
                        menuId="modal-update-status"
                        value={selectedTicket.status || "open"}
                        options={[
                          { value: "open", label: "Open" },
                          { value: "ticket_assigned", label: "Ticket Assigned" },
                          { value: "in_progress", label: "In Progress" },
                          { value: "closed", label: "Closed" },
                        ]}
                        placeholder="Open"
                        disabled={statusUpdating}
                        isOpen={openDropdownId === "modal-status"}
                        onToggle={() => setOpenDropdownId((prev) => (prev === "modal-status" ? null : "modal-status"))}
                        onSelect={(value) => {
                          setOpenDropdownId(null);
                          handleQuickStatusChange(selectedTicket.id, value);
                        }}
                      />
                    </div>

                    {/* Assign Handler */}
                    <div className="tickets-modal__action-card">
                      <label className="tickets-modal__action-label">
                        Assign Handler {selectedTicket.assignedDeptAccount ? `(Current: ${DEPT_DISPLAY_NAMES[selectedTicket.assignedDeptAccount.department] || toTitleCase(selectedTicket.assignedDeptAccount.department)} | ${selectedTicket.assignedDeptAccount.name})` : ""}
                      </label>
                      {modalHandlers.length > 0 ? (
                        <TicketsSelect
                          menuId="modal-assign-handler"
                          value={selectedTicket.assignedDeptAccount?.id || ""}
                          options={[
                            {
                              value: "",
                              label: selectedTicket.assignedDeptAccount
                                ? `${DEPT_DISPLAY_NAMES[selectedTicket.assignedDeptAccount.department] || toTitleCase(selectedTicket.assignedDeptAccount.department)} | ${selectedTicket.assignedDeptAccount.name}`
                                : "Select handler...",
                            },
                            ...modalHandlers.map((acc) => ({
                              value: String(acc.id),
                              label: `${DEPT_DISPLAY_NAMES[acc.department] || toTitleCase(acc.department)} | ${acc.fullName || acc.name || acc.email}`,
                            })),
                          ]}
                          placeholder="Select handler..."
                          disabled={assigningTicketId === selectedTicket.id}
                          isOpen={openDropdownId === "modal-handler"}
                          onToggle={() => setOpenDropdownId((prev) => (prev === "modal-handler" ? null : "modal-handler"))}
                          onSelect={(value) => {
                            setOpenDropdownId(null);
                            if (value) {
                              handleAssignHandler(selectedTicket.id, value);
                            }
                          }}
                        />
                      ) : (
                        <div className="tickets-modal__no-handlers">
                          No handlers available for this department.
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="tickets-card__button tickets-modal__full-detail-btn"
                      onClick={() => {
                        handleCloseTicket();
                        navigate(`/admin/ticket-detail?id=${selectedTicket.id}`);
                      }}
                    >
                      Open Full Detail
                      <ChevronRight size={16} />
                    </button>

                    {aiSummary ? (
                      <div className="tickets-modal__ai-card">
                        <div className="tickets-modal__ai-label">AI Summary</div>
                        <p className="tickets-modal__ai-text">{aiSummary}</p>
                      </div>
                    ) : null}
                  </section>
                </div>

                {/* Conversation Thread */}
                <section className="tickets-modal__convo-section">
                  <div className="tickets-modal__convo-header">
                    Conversation
                  </div>
                  <div className="tickets-modal__convo-body">
                    <div className="tickets-modal__convo-thread">
                      {modalMessagesLoading ? (
                        <div className="tickets-modal__loading">
                          Loading messages...
                        </div>
                      ) : modalMessages.length === 0 ? (
                        <div className="tickets-modal__empty">
                          No messages yet. Send the first reply below.
                        </div>
                      ) : (
                        modalMessages.map((m) => {
                          const presenter = resolveMessagePresenter(m, currentStaffAuthorIds);
                          const isOutgoingAdminMessage = presenter.isOutgoingStaffMessage;
                          const isSystem = presenter.senderType === "system";
                          const displayMessage = m.message
                            ?.replace(/^\[Category: [^\]]+\]\s*/, "")
                            ?.replace(/^\[System\]\s*/, "");

                          const bodyClass = presenter.senderType === "dept"
                            ? "tkt-msg__body tkt-msg__body--dept tkt-msg__body--modal"
                            : presenter.senderType === "admin"
                            ? `tkt-msg__body tkt-msg__body--admin tkt-msg__body--admin-${presenter.adminRoleTone || "default"} tkt-msg__body--modal`
                            : isSystem
                            ? "tkt-msg__body tkt-msg__body--system tkt-msg__body--modal"
                            : "tkt-msg__body tkt-msg__body--member tkt-msg__body--modal";

                          const avatarClass = presenter.senderType === "dept"
                            ? "tkt-msg__avatar tkt-msg__avatar--dept"
                            : presenter.senderType === "admin"
                            ? `tkt-msg__avatar tkt-msg__avatar--admin tkt-msg__avatar--admin-${presenter.adminRoleTone || "default"}`
                            : isSystem
                            ? "tkt-msg__avatar tkt-msg__avatar--system"
                            : "tkt-msg__avatar tkt-msg__avatar--member";

                          return (
                            <div
                              key={m.id}
                              className={`tkt-msg ${isOutgoingAdminMessage ? "tkt-msg--outgoing" : "tkt-msg--member"}`}
                            >
                              <div className={avatarClass}>
                                {presenter.senderType === "dept" ? <Building2 size={11} /> : presenter.senderType === "admin" ? <Shield size={11} /> : isSystem ? <AlertCircle size={11} /> : <User size={11} />}
                              </div>
                              <div className={bodyClass}>
                                <div className="tkt-msg__header tkt-msg__header--modal">
                                  <span className="tkt-msg__author">{presenter.senderName || presenter.senderLabel}</span>
                                  {presenter.senderRoleLabel ? (
                                    <span
                                      className={`tkt-msg__author-role tkt-msg__author-role--${presenter.senderType}${
                                        presenter.senderType === "admin"
                                          ? ` tkt-msg__author-role--admin-${presenter.adminRoleTone || "default"}`
                                          : ""
                                      }`}
                                    >
                                      {presenter.senderRoleLabel}
                                    </span>
                                  ) : null}
                                  <span className="tkt-msg__time">{formatDate(m.createdAt)}</span>
                                </div>
                                <div className="tkt-msg__text tkt-msg__text--modal">{displayMessage}</div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={modalMessagesEndRef} />
                    </div>

                    {/* Reply Composer */}
                    {!isClosedOrResolved ? (
                      <div className="tkt-reply tkt-reply--modal">
                        {modalReplyError ? (
                          <div className="tkt-reply__error">
                            {modalReplyError}
                          </div>
                        ) : null}
                        <div className="tkt-reply__row">
                          <textarea
                            className="tkt-reply__textarea"
                            value={modalReplyText}
                            onChange={(e) => setModalReplyText(e.target.value)}
                            onKeyDown={handleModalKeyDown}
                            placeholder="Type your reply... (Enter to send)"
                            disabled={modalReplySending}
                            rows={2}
                          />
                          <button
                            type="button"
                            className="admin-button is-primary tkt-reply__send"
                            onClick={handleModalSendReply}
                            disabled={modalReplySending || !modalReplyText.trim()}
                          >
                            <Send size={14} />
                            {modalReplySending ? "Sending..." : "Send"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="tkt-reply__closed tkt-reply__closed--modal">
                        <CheckCircle2 size={14} />
                        This ticket is {selectedTicket.status}. Change status to reopen.
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          );
        })()
      ) : null}
    </div>
  );
}
